import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileText, faSpinner, faExclamationTriangle, faTrash } from '@fortawesome/free-solid-svg-icons';
import Modal from '@/components/ui/Modal';
import { APIRoutes } from '@/services/api.routes';
import { showSuccessToast, handleAPIError } from '@/utils/toasts';
import type { Video, VideoKnowledge } from '@/models';

interface VideoKnowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video;
  onVideoUpdated?: (video: Video) => void;
}

const VideoKnowledgeModal: React.FC<VideoKnowledgeModalProps> = ({
  isOpen,
  onClose,
  video,
  onVideoUpdated
}) => {
  const [knowledge, setKnowledge] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Check if video is eligible for knowledge access
      if (!video.providesKnowledge) {
        setError('Dieses Video bietet keine Knowledge-Daten an');
        return;
      }
      if (video.localProcessingState !== 'DONE') {
        setError('Video muss vollständig verarbeitet sein (localProcessingState: DONE)');
        return;
      }
      fetchKnowledge();
    }
  }, [isOpen, video.id]);

  const fetchKnowledge = async () => {
    setLoading(true);
    setError(null);
    try {
      const knowledgeData: VideoKnowledge = await APIRoutes.getVideoKnowledge(video.id);
      setKnowledge(knowledgeData.markdown ?? '');
    } catch (err) {
      console.error('Error fetching video knowledge:', err);
      setError('Fehler beim Laden der Video-Knowledge');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setKnowledge('');
    setError(null);
    onClose();
  };

  const handleDeleteKnowledge = async () => {
    const confirmed = window.confirm(
      'Möchten Sie die Knowledge-Daten wirklich löschen?\n\n' +
      'Diese Aktion ist IRREVERSIBEL und entfernt das Video aus dem LLM und der Knowledge-Base.\n\n' +
      'Das Video selbst bleibt erhalten, aber die extrahierten Knowledge-Daten werden dauerhaft gelöscht.'
    );

    if (!confirmed) return;

    try {
      setDeleting(true);
      await APIRoutes.deleteVideoKnowledge(video.id);

      // Fetch updated video to reflect providesKnowledge=false
      const updatedVideo = await APIRoutes.getVideo(video.id);
      onVideoUpdated?.(updatedVideo);

      showSuccessToast('Knowledge-Daten erfolgreich gelöscht');
      handleClose();
    } catch (error) {
      console.error('Error deleting video knowledge:', error);
      handleAPIError(error, 'Fehler beim Löschen der Knowledge-Daten');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Markdown Knowledge - ${video.title}`}
      icon={<FontAwesomeIcon icon={faFileText} className="w-5 h-5 text-green-600" />}
      maxWidth="4xl"
      closeDisabled={loading}
    >
      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 animate-spin text-blue-600 mr-2" />
            <span>Lade Video-Knowledge...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && knowledge && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Extrahiertes Wissen
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => navigator.clipboard.writeText(knowledge)}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer"
                >
                  In Zwischenablage kopieren
                </button>
                <button
                  onClick={handleDeleteKnowledge}
                  disabled={deleting}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 cursor-pointer flex items-center gap-2"
                  title="Knowledge-Daten löschen"
                >
                  {deleting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      <span>Löschen...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      <span>Löschen</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                {knowledge}
              </pre>
            </div>
          </div>
        )}

        {!loading && !error && !knowledge && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Keine Knowledge-Daten verfügbar.
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VideoKnowledgeModal;
